import axios from 'axios';
import Cookies from 'js-cookie';

// Configuración de la URL base de la API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // En producción, usar rutas relativas (mismo host)
  : 'http://localhost:8000'; // En desarrollo, usar la URL completa del backend

// Configurar axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las requests
api.interceptors.request.use((config) => {
  const token = Cookies.get('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Si es error 401 y NO es una petición de login, redirigir
    // (si es login, dejamos que el componente maneje el error)
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      Cookies.remove('auth_token');
      // Redirigir al login según el entorno
      if (process.env.NODE_ENV === 'production') {
        window.location.href = '/public/login';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  },

  logout: async () => {
    await api.post('/api/auth/logout');
    Cookies.remove('auth_token');
  },

  me: async () => {
    const response = await api.get('/api/auth/me');
    // Manejar tanto el formato estandar {data: ...} como el formato directo {user: ...}
    return response.data.data || response.data;
  },
};

export const userService = {
  getProfile: async () => {
    // Antes apuntaba a /api/clients/me, ruta que no existe: caía en
    // GET /api/clients/{id} con id='me' y devolvía 404. Ahora esa ruta además
    // exige Admin. El endpoint real es /api/auth/me, que responde {user}.
    const response = await api.get('/api/auth/me');
    return response.data.user;
  },

  getBalance: async () => {
    const response = await api.get('/api/credits/balance');
    return response.data.data;
  },

  // Paquetes de créditos (listar y compra)
  getCreditPackages: async () => {
    const response = await api.get('/api/credits/packages');
    return response.data.data;
  },

  purchaseCreditsPackage: async (packageId) => {
    const response = await api.post('/api/credits/purchase', { package_id: packageId });
    return response.data.data;
  },

  // Planes disponibles y cambio de plan
  getAvailablePlans: async () => {
    const response = await api.get('/api/plans');
    return response.data.data;
  },

  upgradePlan: async (planId) => {
    const response = await api.post('/api/plans/upgrade', { plan_id: planId });
    return response.data.data;
  },

  // Precios a granel
  getBulkPricing: async () => {
    const response = await api.get('/api/credits/bulk/pricing');
    return response.data.data;
  },

  getBulkQuote: async (quantity) => {
    const response = await api.get(`/api/credits/bulk/quote?quantity=${quantity}`);
    return response.data.data;
  },

  purchaseBulkCredits: async (quantity) => {
    const response = await api.post('/api/credits/bulk/purchase', { quantity });
    return response.data.data;
  },

  // Nuevo: generar link de pago para plan o compra a granel
  generatePaymentLink: async (payload) => {
    // payload: { email, plan_id } o { email, quantity }
    const response = await api.post('/api/payments/link/generate', payload);
    return response.data.data;
  },

  // Facturas y nóminas
  getInvoices: async (page = 1, skipDoc = null, filters = {}) => {
    const qs = new URLSearchParams();
    // Default pagination values if not provided via page/filters
    const limit = filters.limit || 10;
    const offset = (page - 1) * limit;

    qs.set('limit', limit);
    qs.set('offset', offset);

    if (skipDoc) qs.set('skipdoc', skipDoc);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    if (filters.status) qs.set('status', filters.status);
    if (filters.company_id) qs.set('company_id', filters.company_id);
    if (filters.date_from) qs.set('date_from', filters.date_from);
    if (filters.date_to) qs.set('date_to', filters.date_to);
    if (filters.metodo_pago) qs.set('metodo_pago', filters.metodo_pago);
    if (filters.receiver_id) qs.set('receiver_id', filters.receiver_id);
    const response = await api.get(`/api/invoices?${qs.toString()}`);
    return response.data;
  },

  getNominas: async (page = 1, skipDoc = null, filters = {}) => {
    const qs = new URLSearchParams();
    if (page != null) qs.set('page', page);
    const limit = filters.limit || 10;
    const offset = (page - 1) * limit;
    qs.set('limit', limit);
    qs.set('offset', offset);
    qs.set('tipo_comprobante', 'N');
    if (skipDoc) qs.set('skipdoc', skipDoc);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    if (filters.status) qs.set('status', filters.status);
    if (filters.company_id) qs.set('company_id', filters.company_id);
    if (filters.date_from) qs.set('date_from', filters.date_from);
    if (filters.date_to) qs.set('date_to', filters.date_to);
    const response = await api.get(`/api/invoices?${qs.toString()}`);
    return response.data;
  },

  createInvoice: async (data) => {
    const response = await api.post('/api/invoices', data);
    return response.data;
  },

  updateInvoice: async (id, data) => {
    const response = await api.put(`/api/invoices/${id}`, data);
    return response.data;
  },

  deleteInvoice: async (id) => {
    const response = await api.delete(`/api/invoices/${id}`);
    return response.data;
  },

  stampInvoice: async (id) => {
    const response = await api.post(`/api/invoices/${id}/stamp`);
    return response.data;
  },

  getInvoice: async (id) => {
    const response = await api.get(`/api/invoices/${id}`);
    return response.data;
  },

  // Crear pago en borrador
  createPayment: async (data) => {
    const response = await api.post('/api/payments', data);
    return response.data;
  },

  updatePayment: async (id, data) => {
    const response = await api.put(`/api/payments/${id}`, data);
    return response.data;
  },
  
  // Timbrar pago existentedel usuario
  getPayments: async (limit = 100, offset = 0, filters = {}) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    if (filters.date_from) qs.set('date_from', filters.date_from);
    if (filters.date_to) qs.set('date_to', filters.date_to);
    if (filters.invoice_id) qs.set('invoice_id', filters.invoice_id);
    const response = await api.get(`/api/payments?${qs.toString()}`);
    return response.data;
  },

  // Timbrar pago existente
  stampPayment: async (id) => {
    const response = await api.post(`/api/payments/${id}/stamp`);
    return response.data;
  },

  // Eliminar pago
  deletePayment: async (id) => {
    const response = await api.delete(`/api/payments/${id}`);
    return response.data;
  },
  
  downloadInvoiceXml: async (id) => {
    return await api.get(`/api/invoices/${id}/xml`, { responseType: 'arraybuffer' });
  },

  generateInvoicePdf: async (id, options = {}) => {
    const resp = await api.post(`/api/invoices/${id}/pdf`, options);
    return resp.data;
  },
  
  previewInvoicePdf: async (id, options = {}) => {
    // Si se pasa un objeto en 'id' en lugar de un string, usamos el endpoint raw
    if (typeof id === 'object') {
        const resp = await api.post('/api/invoices/preview-raw', {
            invoice_data: id,
            ...options
        });
        return resp.data;
    }
    const resp = await api.post(`/api/invoices/${id}/pdf/preview`, options);
    return resp.data;
  },

  generatePaymentsPdf: async (id, options = {}) => {
    const resp = await api.post(`/api/payments/${id}/pdf`, options);
    return resp.data;
  },
  downloadPaymentsXml: async (id) => {
    return await api.get(`/api/payments/${id}/xml`, { responseType: 'arraybuffer' });
  },

  getCancellationMotives: async () => {
    const resp = await api.get('/api/cancellation/motives');
    return resp.data;
  },

  checkInvoiceCancellation: async (id) => {
    const resp = await api.get(`/api/invoices/${id}/cancellation/check`);
    return resp.data;
  },

  cancelInvoice: async (id, payload) => {
    const resp = await api.post(`/api/invoices/${id}/cancel`, payload);
    return resp.data;
  },

  getCancellationStatus: async (id) => {
    const resp = await api.get(`/api/invoices/${id}/cancellation/status`);
    return resp.data;
  },

  checkPaymentCancellation: async (id) => {
    const resp = await api.get(`/api/payments/${id}/cancellation/check`);
    return resp.data;
  },

  cancelPayment: async (id, payload) => {
    const resp = await api.post(`/api/payments/${id}/cancel`, payload);
    return resp.data;
  },

  getPaymentCancellationStatus: async (id) => {
    const resp = await api.get(`/api/payments/${id}/cancellation/status`);
    return resp.data;
  },

  sendInvoiceEmail: async (id, payload) => {
    const resp = await api.post(`/api/invoices/${id}/email`, payload);
    return resp.data;
  },

  getInvoiceTaxCalculation: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) {
      params.set('date_from', filters.date_from);
    }
    if (filters.date_to) {
      params.set('date_to', filters.date_to);
    }
    if (filters.company_id) {
      params.set('company_id', filters.company_id);
    }
    const queryString = params.toString();
    const url = `/api/invoices/tax-calculation${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  syncSatDocuments: async (companyId, filters = {}) => {
    const payload = {};
    if (filters.date_from) payload.date_from = filters.date_from;
    if (filters.date_to) payload.date_to = filters.date_to;
    const response = await api.post(`/api/companies/${companyId}/sat-sync`, payload);
    return response.data;
  },

  getCobranzaPpdInvoices: async (limit = 20, offset = 0, filters = {}) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    if (filters.company_id) qs.set('company_id', filters.company_id);
    if (filters.receiver_id) qs.set('receiver_id', filters.receiver_id);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    const response = await api.get(`/api/collections/ppd/invoices?${qs.toString()}`);
    return response.data;
  },

  stampBulkPpdPayments: async (payload) => {
    const response = await api.post('/api/collections/ppd/payments/stamp-bulk', payload);
    return response.data;
  },

  getCobranzaPpdBatches: async (limit = 10, offset = 0) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    const response = await api.get(`/api/collections/ppd/batches?${qs.toString()}`);
    return response.data;
  },
};

export const adminService = {
  // Usuarios
  getUsers: async (page = 1, search = '') => {
    const response = await api.get(`/api/admin/users?page=${page}&search=${search}`);
    return response.data;
  },

  getUser: async (id) => {
    const response = await api.get(`/api/admin/users/${id}`);
    return response.data.data;
  },

  getRecentUsers: async (limit = 10) => {
    const response = await api.get(`/api/admin/users/recent?limit=${limit}`);
    return response.data.data;
  },

  createUser: async (data) => {
    const response = await api.post('/api/admin/users', data);
    return response.data.data;
  },

  updateUser: async (id, data) => {
    const response = await api.put(`/api/admin/users/${id}`, data);
    return response.data.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/api/admin/users/${id}`);
    return response.data;
  },

  toggleUserStatus: async (id, isActive) => {
    const response = await api.patch(`/api/admin/users/${id}/status`, { is_active: isActive });
    return response.data.data;
  },

  // Gestión de planes de usuarios
  changeUserPlan: async (id, planId) => {
    await api.post(`/api/admin/users/${id}/change-plan`, { plan_id: planId });
  },

  addBulkCredits: async (id, credits) => {
    await api.post(`/api/admin/users/${id}/credits`, { credits });
  },

  addCredits: async (id, credits) => {
    await api.post(`/api/admin/users/${id}/credits`, { credits });
  },

  // Planes
  getPlans: async () => {
    const response = await api.get('/api/admin/plans');
    return response.data.data;
  },

  getPlan: async (id) => {
    const response = await api.get(`/api/admin/plans/${id}`);
    return response.data.data;
  },

  createPlan: async (data) => {
    const response = await api.post('/api/admin/plans', data);
    return response.data.data;
  },

  updatePlan: async (id, data) => {
    const response = await api.put(`/api/admin/plans/${id}`, data);
    return response.data.data;
  },

  deletePlan: async (id) => {
    const response = await api.delete(`/api/admin/plans/${id}`);
    return response.data;
  },

  // Estadísticas
  getStats: async () => {
    const response = await api.get('/api/admin/stats');
    return response.data.data;
  },

  getMonthlyStats: async () => {
    const response = await api.get('/api/admin/stats/monthly');
    return response.data.data;
  },

  getTopUsers: async (limit = 10) => {
    const response = await api.get(`/api/admin/stats/top-users?limit=${limit}`);
    return response.data.data;
  },
  regenerateInvoiceTaxes: async () => {
    const response = await api.post('/api/admin/invoices/regenerate-taxes');
    return response.data.data;
  },
  regenerateInvoiceTaxesForUser: async (id) => {
    const response = await api.post(`/api/admin/users/${id}/invoices/regenerate-taxes`);
    return response.data.data;
  },

  uploadCaptureLine: async (formData) => {
    const response = await api.post('/api/admin/capture-lines', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const productsService = {
  getProducts: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/api/productos-servicios${params}`);
    return response.data;
  },

  getProduct: async (id) => {
    const response = await api.get(`/api/productos-servicios/${id}`);
    return response.data.data;
  },

  createProduct: async (data) => {
    const response = await api.post('/api/productos-servicios', data);
    return response.data.data;
  },

  updateProduct: async (id, data) => {
    const response = await api.put(`/api/productos-servicios/${id}`, data);
    return response.data.data;
  },

  deleteProduct: async (id) => {
    const response = await api.delete(`/api/productos-servicios/${id}`);
    return response.data;
  },

  searchProducts: async (search, limit = 20) => {
    const response = await api.get(`/api/productos-servicios/search?q=${encodeURIComponent(search)}&limit=${limit}`);
    return response.data;
  },

  importProductsCsv: async (formData) => {
    const response = await api.post('/api/productos-servicios/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const receiversService = {
  getReceivers: async (search = '', options = {}) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (options.includeEmployees) qs.set('include_employees', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/api/receivers${suffix}`);
    return response.data;
  },

  getReceiver: async (id) => {
    const response = await api.get(`/api/receivers/${id}`);
    return response.data.data;
  },

  createReceiver: async (data) => {
    const response = await api.post('/api/receivers', data);
    return response.data.data;
  },

  updateReceiver: async (id, data) => {
    const response = await api.put(`/api/receivers/${id}`, data);
    return response.data.data;
  },

  deleteReceiver: async (id) => {
    const response = await api.delete(`/api/receivers/${id}`);
    return response.data;
  },
};

export const employeesService = {
  getEmployees: async (search = '', companyId = null) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (companyId) qs.set('company_id', companyId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/api/employees${suffix}`);
    return response.data;
  },

  getEmployee: async (id) => {
    const response = await api.get(`/api/employees/${id}`);
    return response.data.data;
  },

  createEmployee: async (data) => {
    const response = await api.post('/api/employees', data);
    return response.data.data;
  },

  updateEmployee: async (id, data) => {
    const response = await api.put(`/api/employees/${id}`, data);
    return response.data.data;
  },

  deleteEmployee: async (id) => {
    const response = await api.delete(`/api/employees/${id}`);
    return response.data;
  },
};

export const catalogService = {
  searchProducts: async (search, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (limit) params.append('limit', limit);
    
    const response = await api.get(`/api/catalogs/productos?${params.toString()}`);
    return response.data;
  },

  searchUnits: async (search, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (limit) params.append('limit', limit);
    
    const response = await api.get(`/api/catalogs/unidades?${params.toString()}`);
    return response.data;
  },

  getProduct: async (id) => {
    const response = await api.get(`/api/catalogs/productos/${id}`);
    return response.data;
  },

  getUnit: async (id) => {
    const response = await api.get(`/api/catalogs/unidades/${id}`);
    return response.data;
  },

  validateProduct: async (id) => {
    const response = await api.post('/api/catalogs/productos/validate', { id });
    return response.data;
  },

  validateUnit: async (id) => {
    const response = await api.post('/api/catalogs/unidades/validate', { id });
    return response.data;
  },
};

export const folioSerieService = {
  getFolioSeries: async (companyId, filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    
    const response = await api.get(`/api/companies/${companyId}/folio-series?${params.toString()}`);
    return response.data;
  },

  getFolioSerie: async (companyId, id) => {
    const response = await api.get(`/api/companies/${companyId}/folio-series/${id}`);
    return response.data;
  },

  createFolioSerie: async (companyId, data) => {
    const response = await api.post(`/api/companies/${companyId}/folio-series`, data);
    return response.data;
  },

  updateFolioSerie: async (companyId, id, data) => {
    const response = await api.put(`/api/companies/${companyId}/folio-series/${id}`, data);
    return response.data;
  },

  deleteFolioSerie: async (companyId, id) => {
    const response = await api.delete(`/api/companies/${companyId}/folio-series/${id}`);
    return response.data;
  },

  toggleStatus: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/folio-series/${id}/toggle-status`);
    return response.data;
  },

  setAsDefault: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/folio-series/${id}/set-default`);
    return response.data;
  },
};

export const companiesService = {
  getCompanies: async () => {
    const response = await api.get('/api/companies');
    return response.data;
  },

  getCompany: async (id) => {
    const response = await api.get(`/api/companies/${id}`);
    return response.data;
  },

  createCompany: async (data) => {
    const response = await api.post('/api/companies', data);
    return response.data;
  },

  updateCompany: async (id, data) => {
    const response = await api.put(`/api/companies/${id}`, data);
    return response.data;
  },

  deleteCompany: async (id) => {
    const response = await api.delete(`/api/companies/${id}`);
    return response.data;
  },
};

export const businessActivitiesService = {
  getBusinessActivities: async () => {
    const response = await api.get('/api/business-activities');
    return response.data;
  },
};

export const autofacturaConfigsService = {
  getConfigs: async () => {
    const response = await api.get('/api/autofactura-configs');
    return response.data;
  },

  createConfig: async (data) => {
    const response = await api.post('/api/autofactura-configs', data);
    return response.data;
  },

  updateConfig: async (id, data) => {
    const response = await api.put(`/api/autofactura-configs/${id}`, data);
    return response.data;
  },

  deleteConfig: async (id) => {
    const response = await api.delete(`/api/autofactura-configs/${id}`);
    return response.data;
  },

  regenerateToken: async (id) => {
    const response = await api.post(`/api/autofactura-configs/${id}/regenerate-token`);
    return response.data;
  },

  sendLinkEmail: async (token) => {
    const response = await api.post(`/autofactura/${encodeURIComponent(token)}/send-link`);
    return response.data;
  },
};

export const certificatesService = {
  getCertificates: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/certificates`);
    return response.data;
  },

  getCertificate: async (companyId, id) => {
    const response = await api.get(`/api/companies/${companyId}/certificates/${id}`);
    return response.data;
  },

  uploadCertificate: async (companyId, formData) => {
    const response = await api.post(`/api/companies/${companyId}/certificates`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteCertificate: async (companyId, id) => {
    const response = await api.delete(`/api/companies/${companyId}/certificates/${id}`);
    return response.data;
  },

  toggleStatus: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/certificates/${id}/toggle-status`);
    return response.data;
  },
};

export const efirmaService = {
  getEfirma: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/efirma`);
    return response.data;
  },

  uploadEfirma: async (companyId, formData) => {
    const response = await api.post(`/api/companies/${companyId}/efirma`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteEfirma: async (companyId) => {
    const response = await api.delete(`/api/companies/${companyId}/efirma`);
    return response.data;
  },
};

export const fiscalAdminService = {
  // Panel fiscal: clientes que alcanza el actor autenticado
  getMiCartera: async (params = {}) => {
    const response = await api.get('/api/fiscal/cartera', { params });
    return response.data.data;
  },

  // Agenda del periodo: qué declaraciones faltan este mes.
  // La fila es la EMPRESA, no el cliente: la declaración cuelga del RFC.
  // Sin `period` el backend devuelve el mes anterior, que es el que se declara.
  getAgenda: async (params = {}) => {
    const response = await api.get('/api/fiscal/declaraciones', { params });
    return response.data.data;
  },

  // Relación mensual de declaraciones de UN fiscal-admin, para cobrarle.
  //
  // SOLO ADMIN: el servidor lo impone con AdminMiddleware. Es la base de lo que
  // se le cobra al gestor, así que él no puede generársela. Se pide para
  // alguien en concreto porque se cobra por gestor.
  //
  // Mismo alcance y mismo criterio de "presentada" que la agenda de ese gestor:
  // el archivo y la pantalla cuentan lo mismo.
  descargarReporteDeclaraciones: async (fiscalAdminId, period) => {
    const response = await api.get(
      `/api/admin/fiscal-admins/${fiscalAdminId}/declaraciones/reporte`,
      {
        params: period ? { period } : {},
        responseType: 'blob',
      }
    );
    return response;
  },

  // Administración (solo Admin)
  listFiscalAdmins: async () => {
    const response = await api.get('/api/admin/fiscal-admins');
    return response.data.data;
  },

  createFiscalAdmin: async (data) => {
    const response = await api.post('/api/admin/fiscal-admins', data);
    return response.data.data;
  },

  // La cartera se administra por USUARIOS asignados, no por planes.
  updateCartera: async (id, clientIds) => {
    const response = await api.put(`/api/admin/fiscal-admins/${id}/cartera`, { client_ids: clientIds });
    return response.data.data;
  },

  resetPassword: async (id, password) => {
    const response = await api.put(`/api/admin/fiscal-admins/${id}/password`, { password });
    return response.data;
  },

  removeFiscalAdmin: async (id) => {
    const response = await api.delete(`/api/admin/fiscal-admins/${id}`);
    return response.data;
  },

  // Usuarios no-staff sin fiscal-admin asignado (hueco de gestión).
  getUsuariosSinGestor: async () => {
    const response = await api.get('/api/admin/fiscal-admins/usuarios-sin-gestor');
    return response.data.data;
  },

  // Candidatos a asignar a la cartera de un fiscal-admin.
  getAssignableUsers: async (search = '') => {
    const response = await api.get(`/api/admin/users?limit=100&search=${encodeURIComponent(search)}`);
    return response.data?.data?.users || [];
  },
};

// Datos fiscales operados en nombre de un cliente de la cartera.
// El alcance lo decide el backend; aquí no se filtra nada.
export const fiscalDataService = {
  // --- Clientes, catálogo y facturas del contribuyente, operados por staff en su
  //     nombre. Rutas propias bajo la empresa: los endpoints del cliente no se
  //     tocan. Las facturas son solo lectura. ---
  getReceivers: async (companyId, search = '') => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/api/fiscal/companies/${companyId}/receivers${qs}`);
    return response.data.data;
  },

  createReceiver: async (companyId, data) => {
    const response = await api.post(`/api/fiscal/companies/${companyId}/receivers`, data);
    return response.data.data;
  },

  updateReceiver: async (companyId, id, data) => {
    const response = await api.put(`/api/fiscal/companies/${companyId}/receivers/${id}`, data);
    return response.data.data;
  },

  deleteReceiver: async (companyId, id) => {
    const response = await api.delete(`/api/fiscal/companies/${companyId}/receivers/${id}`);
    return response.data;
  },

  getProducts: async (companyId, search = '') => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/api/fiscal/companies/${companyId}/products${qs}`);
    return response.data.data;
  },

  createProduct: async (companyId, data) => {
    const response = await api.post(`/api/fiscal/companies/${companyId}/products`, data);
    return response.data.data;
  },

  updateProduct: async (companyId, id, data) => {
    const response = await api.put(`/api/fiscal/companies/${companyId}/products/${id}`, data);
    return response.data.data;
  },

  deleteProduct: async (companyId, id) => {
    const response = await api.delete(`/api/fiscal/companies/${companyId}/products/${id}`);
    return response.data;
  },

  getInvoices: async (companyId, params = {}) => {
    const response = await api.get(`/api/fiscal/companies/${companyId}/invoices`, { params });
    return response.data.data;
  },

  // Devuelve SIEMPRE colección: un RFC puede resolver a varias empresas.
  searchByRfc: async (rfc) => {
    const response = await api.get(`/api/fiscal/companies?rfc=${encodeURIComponent(rfc)}`);
    return response.data.data;
  },

  getCompany: async (companyId) => {
    const response = await api.get(`/api/fiscal/companies/${companyId}`);
    return response.data.data;
  },

  updateCompany: async (companyId, data) => {
    const response = await api.put(`/api/fiscal/companies/${companyId}`, data);
    return response.data.data;
  },

  createTax: async (companyId, data) => {
    const response = await api.post(`/api/fiscal/companies/${companyId}/taxes`, data);
    return response.data.data;
  },

  updateTax: async (companyId, taxId, data) => {
    const response = await api.put(`/api/fiscal/companies/${companyId}/taxes/${taxId}`, data);
    return response.data.data;
  },

  deleteTax: async (companyId, taxId) => {
    const response = await api.delete(`/api/fiscal/companies/${companyId}/taxes/${taxId}`);
    return response.data;
  },

  // Líneas de captura y estimación reutilizan los endpoints existentes: el
  // backend resuelve el acceso por actor, no por dueño.
  getCaptureLines: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/capture-lines`);
    return response.data;
  },

  // `consecutivo` opcional: sin él llega la versión vigente del periodo; con él,
  // esa versión exacta (0 = declaración normal, 1..N = complementarias).
  downloadCaptureLine: async (companyId, period, consecutivo) => {
    const params = consecutivo === undefined || consecutivo === null
      ? undefined
      : { consecutivo };

    return api.get(
      `/api/companies/${companyId}/capture-lines/period/${encodeURIComponent(period)}/download`,
      { responseType: 'blob', params }
    );
  },

  // `consecutivo` opcional: 0 (u omitido) es la declaración normal, 1..N las
  // complementarias. Va en la petición y no lo asigna el servidor, para que
  // reintentar la misma versión siga siendo un rechazo y no cree una nueva.
  uploadCaptureLine: async ({ userId, rfc, amount, period, pdf, consecutivo }) => {
    const form = new FormData();
    form.append('user_id', userId);
    form.append('rfc', rfc);
    form.append('amount', amount);
    form.append('period', period);
    form.append('pdf', pdf);
    if (consecutivo !== undefined && consecutivo !== null) {
      form.append('consecutivo', String(consecutivo));
    }
    const response = await api.post('/api/admin/capture-lines', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getTaxEstimation: async (companyId, dateFrom, dateTo) => {
    const params = new URLSearchParams({ company_id: companyId });
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await api.get(`/api/invoices/tax-calculation?${params.toString()}`);
    return response.data;
  },
};

// Dispositivos, bitácora y constancias de e.firma.
export const efirmaAdminService = {
  listDevices: async () => {
    const response = await api.get('/api/admin/efirma-devices');
    return response.data.data;
  },

  authorizeDevice: async (id) => {
    const response = await api.put(`/api/admin/efirma-devices/${id}/authorize`);
    return response.data.data;
  },

  revokeDevice: async (id) => {
    const response = await api.put(`/api/admin/efirma-devices/${id}/revoke`);
    return response.data.data;
  },

  // Cupo de exportaciones/hora del dispositivo. null lo retira: el equipo vuelve
  // al default del sistema. Solo aplica a dispositivos autorizados.
  setDeviceCupo: async (id, cupoHora) => {
    const response = await api.put(`/api/admin/efirma-devices/${id}/cupo`, {
      cupo_hora: cupoHora,
    });
    return response.data.data;
  },

  // Solo lectura: la bitácora no se modifica ni se borra desde la aplicación.
  getAccessLog: async (filtros = {}) => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });
    const qs = params.toString();
    const response = await api.get(`/api/admin/efirma/access-log${qs ? `?${qs}` : ''}`);
    return response.data.data;
  },

  listConsents: async () => {
    const response = await api.get('/api/admin/efirma/consents');
    return response.data.data;
  },

  recordConsent: async (data) => {
    const response = await api.post('/api/admin/efirma/consents', data);
    return response.data.data;
  },
};

export const efirmaFiscalService = {
  getCompanyStatus: async (companyId) => {
    const response = await api.get(`/api/fiscal/companies/${companyId}/efirma-status`);
    return response.data.data;
  },
};

/**
 * Programación de descargas del SAT.
 *
 * Dos alcances, dos rutas: el Admin alcanza todos los RFC con e.firma y el
 * fiscal-admin solo su cartera. Quién alcanza qué lo decide el servidor a partir
 * del token; aquí solo se elige la ruta del perfil en el que estamos.
 *
 * El GET no toca SW: es lo que llena el modal antes de ejecutar nada. El POST
 * procesa una tanda, con tope que impone el servidor (`lote_maximo`).
 */
/**
 * Conciliación de IVA de un contribuyente: por mes, trasladado, acreditable,
 * saldo a cargo o a favor, acreditamiento de periodos anteriores e IVA por pagar.
 *
 * `getConciliacion` solo lee. `calcularPeriodo` calcula UN mes, porque la consulta
 * de documentos a SW está limitada a 31 días, y no calcula nada mientras el
 * histórico de 24 meses no esté descargado.
 */
export const conciliacionIvaService = {
  getConciliacion: async (companyId) => {
    const response = await api.get(`/api/fiscal/companies/${companyId}/conciliacion-iva`);
    return response.data;
  },

  calcularPeriodo: async (companyId, period) => {
    const response = await api.post(`/api/fiscal/companies/${companyId}/conciliacion-iva/${period}`);
    return response.data;
  },

  // Reescribe la cadena persistida sin recalcular ningún mes. Para el
  // contribuyente que ya tiene sus 24 meses calculados y por tanto nada que
  // postear: sin esto su acumulado se quedaría sin persistir para siempre.
  reencadenar: async (companyId) => {
    const response = await api.post(`/api/fiscal/companies/${companyId}/conciliacion-iva/cadena`);
    return response.data;
  },
};

/**
 * Estimación de IVA de un periodo partiendo del acumulado conciliado.
 *
 * SOLO ADMIN FISCAL. Responde «cuánto tiene que pagar este mes» contando el
 * arrastre: mientras quede acumulado, un mes a cargo se paga en 0.
 *
 * No es el mismo dato que `fiscalDataService.getTaxEstimation`: ése calcula el
 * IVA acreditable con la política por actividad y sin acumulado, y es el que ven
 * el contribuyente y OrquesTax. Las dos cifras pueden diferir, y la respuesta de
 * ésta declara su base y su regla para que se pueda saber por qué.
 */
export const estimacionIvaService = {
  get: async (companyId, period) => {
    const response = await api.get(
      `/api/fiscal/companies/${companyId}/estimacion-iva?period=${encodeURIComponent(period)}`
    );
    return response.data;
  },
};

/**
 * Histórico de descargas del SAT de un contribuyente: 24 meses en 8 ventanas de
 * tres meses, para poder conciliar IVA acreditable contra IVA a pagar.
 *
 * `getEstado` solo lee: devuelve el estado de las 8 ventanas sin crear nada.
 * `solicitarVentana` atiende UNA ventana — el histórico completo son hasta 17
 * llamadas a SW y en una sola petición se rompería a mitad—, así que quien llama
 * recorre las que falten.
 */
export const historicoDescargasService = {
  getEstado: async (companyId) => {
    const response = await api.get(`/api/fiscal/companies/${companyId}/historico-descargas`);
    return response.data;
  },

  solicitarVentana: async (companyId, ventana) => {
    const response = await api.post(`/api/fiscal/companies/${companyId}/historico-descargas`, {
      ventana,
    });
    return response.data;
  },
};

export const programacionDescargasService = {
  getAlcanceAdmin: async () => {
    const response = await api.get('/api/admin/programaciones-descarga');
    return response.data;
  },

  procesarAdmin: async (companyIds) => {
    const response = await api.post('/api/admin/programaciones-descarga', {
      company_ids: companyIds,
    });
    return response.data;
  },

  getAlcanceFiscal: async () => {
    const response = await api.get('/api/fiscal/programaciones-descarga');
    return response.data;
  },

  procesarFiscal: async (companyIds) => {
    const response = await api.post('/api/fiscal/programaciones-descarga', {
      company_ids: companyIds,
    });
    return response.data;
  },
};

export const captureLinesService = {
  listByCompany: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/capture-lines`);
    return response.data;
  },
  getByPeriod: async (companyId, period) => {
    const response = await api.get(`/api/companies/${companyId}/capture-lines/period/${encodeURIComponent(period)}`);
    return response.data;
  },
  // Sin `consecutivo` entrega la VIGENTE, como siempre. Con él, la versión
  // exacta: hace falta para interpretar la que sustenta el saldo sin depender
  // de que sea la última.
  downloadByPeriod: async (companyId, period, consecutivo = null) => {
    const sufijo = consecutivo === null || consecutivo === undefined
      ? ''
      : `?consecutivo=${encodeURIComponent(consecutivo)}`;
    const response = await api.get(`/api/companies/${companyId}/capture-lines/period/${encodeURIComponent(period)}/download${sufijo}`, {
      responseType: 'blob',
    });
    return response;
  },
};

// Saldo a favor de IVA que se arrastra entre periodos.
//
// El saldo NO se almacena: el servidor lo DERIVA sumando lo que declaró cada
// acuse. Por eso no hay método para "fijar" un saldo; solo se reporta lo que
// dice un acuse y el total se recalcula solo.
export const saldoIvaService = {
  // Devuelve el saldo, su desglose, su rango, sus huecos y
  // `acuses_sin_registro`: los acuses que todavía nadie ha interpretado.
  get: async (companyId, period) => {
    const response = await api.get(
      `/api/companies/${companyId}/saldo-iva?period=${encodeURIComponent(period)}`
    );
    return response.data.data;
  },

  // Reporta lo que dice un acuse. El servidor valida el RFC y el periodo
  // leídos contra los que ya tiene registrados antes de aceptarlo.
  reportarImportes: async (companyId, captureLineId, interpretacion) => {
    const response = await api.post(
      `/api/companies/${companyId}/capture-lines/${captureLineId}/importes`,
      interpretacion
    );
    return response.data.data;
  },
};

// Salud SAT: constancia de situación fiscal y opinión de cumplimiento.
// Documentos del estado del RFC, sin periodo. Solo el staff fiscal los carga;
// el contribuyente únicamente los descarga.
export const saludSatService = {
  // Devuelve SIEMPRE los dos tipos, cargados o no, más `puede_cargar`: quién
  // ve el control de carga lo decide el servidor, no el rol adivinado aquí.
  listByCompany: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/salud-sat`);
    return response.data.data;
  },

  upload: async (companyId, kind, file) => {
    const form = new FormData();
    form.append('documento', file);
    const response = await api.post(`/api/companies/${companyId}/salud-sat/${kind}`, form);
    return response.data.data;
  },

  download: async (companyId, kind) => {
    const response = await api.get(`/api/companies/${companyId}/salud-sat/${kind}/download`, {
      responseType: 'blob',
    });
    return response;
  },
};

export const companyTaxService = {
  getTaxes: async (companyId, filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined) {
        params.append(key, filters[key]);
      }
    });
    const queryString = params.toString();
    const url = `/api/companies/${companyId}/taxes${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  getTax: async (companyId, id) => {
    const response = await api.get(`/api/companies/${companyId}/taxes/${id}`);
    return response.data;
  },

  createTax: async (companyId, data) => {
    const response = await api.post(`/api/companies/${companyId}/taxes`, data);
    return response.data;
  },

  updateTax: async (companyId, id, data) => {
    const response = await api.put(`/api/companies/${companyId}/taxes/${id}`, data);
    return response.data;
  },

  deleteTax: async (companyId, id) => {
    const response = await api.delete(`/api/companies/${companyId}/taxes/${id}`);
    return response.data;
  },

  toggleStatus: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/taxes/${id}/toggle-status`);
    return response.data;
  },

  getCatalogs: async () => {
    const response = await api.get('/api/tax-catalogs');
    return response.data;
  },
};

export { default as taxTemplateService } from './taxTemplateService';
export default api;
